/* eslint-disable @next/next/no-img-element */
import { useDateTimeFormat } from '@graphcommerce/next-ui'
import Head from 'next/head'
import type { CSSProperties, ReactNode } from 'react'
import type { AuthorByline } from '../../lib/content/authors'
import {
  ContentBlock,
  ImageAspect,
  ImageRef,
  ParagraphBlock,
} from '../../lib/content/fetchPublishedPage'
import { PageMeta } from '../Store/PageMeta/PageMeta'
import styles from './PublishedPage.module.css'

export type PublishedPageProps = {
  h1: string
  heroDescription: string
  heroImage: ImageRef
  metaTitle: string
  metaDescription: string
  canonicalPath: string
  author?: AuthorByline
  eyebrow?: string
  publishedAt?: string
  blocks: ContentBlock[]
  toolEmbedUrl?: string | null
}

// Source Serif 4 is only used by this template, so it's loaded here rather
// than site-wide. Roboto (byline, captions) is already loaded globally.
const SERIF_FONT_URL =
  'https://fonts.googleapis.com/css2?family=Source+Serif+4:ital,opsz,wght@0,8..60,400;0,8..60,600;0,8..60,700;1,8..60,400&display=swap'

const SHAPE_RATIO: Record<Exclude<ImageAspect, 'original'>, string> = {
  '16:9': '16 / 9',
  '3:2': '3 / 2',
  '4:3': '4 / 3',
  '1:1': '1 / 1',
  '4:5': '4 / 5',
}

// An image without an `aspect` uses its slot's default shape: 3:2 for hero
// and single images, 1:1 for split. "original" shows it uncropped.
function frameStyle(image: ImageRef, fallback: ImageAspect): CSSProperties {
  const aspect = image.aspect ?? fallback
  if (aspect === 'original') return { display: 'block', width: '100%', height: 'auto' }
  return {
    display: 'block',
    width: '100%',
    aspectRatio: SHAPE_RATIO[aspect],
    objectFit: 'cover',
    objectPosition: image.focus ? `${image.focus.x}% ${image.focus.y}%` : '50% 50%',
  }
}

// Split blocks keep both images the same shape so they line up.
function splitShape(images: ImageRef[]): ImageAspect {
  const aspect = images.find((image) => image.aspect)?.aspect
  return aspect && aspect !== 'original' ? aspect : '1:1'
}

const BLANK_PARAGRAPH = /<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>/g
const TRAILING_BLANK_PARAGRAPH = /<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>\s*$/

// Blank lines at the end of a block are deliberate spacing: each adds one
// line of space below it. Blank lines anywhere else are dropped, since the
// template sets paragraph spacing.
function splitTrailingSpace(html: string): { body: string; extraLines: number } {
  let body = html.trim()
  let extraLines = 0
  while (TRAILING_BLANK_PARAGRAPH.test(body)) {
    body = body.replace(TRAILING_BLANK_PARAGRAPH, '').trimEnd()
    extraLines++
  }
  return { body: body.replace(BLANK_PARAGRAPH, ''), extraLines }
}

function readMinutes(blocks: ContentBlock[]): number {
  const words = blocks
    .filter((block): block is ParagraphBlock => block.type === 'paragraph')
    .reduce(
      (total, block) =>
        total + block.html.replace(/<[^>]*>/g, ' ').split(/\s+/).filter(Boolean).length,
      0,
    )
  return Math.max(1, Math.round(words / 200))
}

function Figure({ children, caption }: { children: ReactNode; caption?: string }) {
  return (
    <figure className={styles.figure}>
      {children}
      {caption && <figcaption className={styles.caption}>{caption}</figcaption>}
    </figure>
  )
}

function Block({ block }: { block: ContentBlock }) {
  if (block.type === 'paragraph') {
    const { body, extraLines } = splitTrailingSpace(block.html)
    if (!body) return null
    return (
      <div
        className={styles.body}
        style={extraLines ? { marginBottom: `${((extraLines + 1) * 1.62).toFixed(2)}em` } : undefined}
        // Content is authored by our own Page Publisher tool (not user input)
        // and served by templi-orders-engine's Page Publisher API.
        dangerouslySetInnerHTML={{ __html: body }}
      />
    )
  }

  if (block.layout === 'single') {
    const image = block.images[0]
    return (
      <Figure caption={image.alt}>
        <img src={image.url} alt={image.alt} loading='lazy' style={frameStyle(image, '3:2')} />
      </Figure>
    )
  }

  const shape = splitShape(block.images)
  return (
    <Figure caption={block.images.map((image) => image.alt).filter(Boolean).join(' · ')}>
      <div className={styles.split}>
        {block.images.map((image) => (
          <img
            key={image.url}
            src={image.url}
            alt={image.alt}
            loading='lazy'
            style={frameStyle({ ...image, aspect: shape }, shape)}
          />
        ))}
      </div>
    </Figure>
  )
}

/**
 * Renders a Page Publisher page (`/{section}/{slug}`) in the editorial
 * template — the same design the Page Publisher dashboard previews, so what
 * the marketer sees there is what goes live. It sits inside the site's
 * normal header/footer (LayoutNavigation) but deliberately doesn't use the
 * storefront's type tokens or card chrome for the article itself.
 */
export function PublishedPage(props: PublishedPageProps) {
  const {
    h1,
    heroDescription,
    heroImage,
    metaTitle,
    metaDescription,
    canonicalPath,
    author,
    eyebrow,
    publishedAt,
    blocks,
    toolEmbedUrl,
  } = props

  const dateFormatter = useDateTimeFormat({ year: 'numeric', month: 'long', day: 'numeric' })
  const meta = [
    publishedAt ? dateFormatter.format(new Date(publishedAt)) : null,
    `${readMinutes(blocks)} min read`,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <>
      <PageMeta title={metaTitle} metaDescription={metaDescription} canonical={canonicalPath} />
      <Head>
        <link rel='stylesheet' href={SERIF_FONT_URL} />
      </Head>

      <article className={styles.article}>
        <div className={styles.column}>
          <span className={styles.eyebrow}>{eyebrow || 'Templi'}</span>
          <h1 className={styles.h1}>{h1}</h1>
          <p className={styles.dek}>{heroDescription}</p>

          <div className={styles.byline}>
            {author?.photo && <img src={author.photo} alt='' className={styles.avatar} />}
            <div>
              <div className={styles.bylineName}>{author?.name || 'Templi Team'}</div>
              <div className={styles.bylineMeta}>{meta}</div>
            </div>
          </div>

          <Figure>
            <img src={heroImage.url} alt={heroImage.alt} style={frameStyle(heroImage, '3:2')} />
          </Figure>

          {blocks.map((block) => (
            <Block key={block.id} block={block} />
          ))}

          {toolEmbedUrl && (
            <div className={styles.embed}>
              <iframe
                src={toolEmbedUrl}
                title={h1}
                width='100%'
                height='100%'
                allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                allowFullScreen
                frameBorder='0'
              />
            </div>
          )}
        </div>
      </article>
    </>
  )
}
